import { useEffect } from 'react';
import { ApiError } from '../services/api';
import { navigateToAngular } from '../services/navigate';
import { notifyError } from '../services/notify';

interface QueryState {
  error: unknown;
  isFetching: boolean;
  isFetchedAfterMount: boolean;
}

/** Query meta for lookups whose 400 the page handles itself (redirect + its own toast). */
export const NOT_FOUND_META = { suppressErrorStatuses: [400] };

/**
 * The API answers both "missing" and "no access" with a 400; other failures keep the
 * global toast. Requires a fetch from this mount so a stale cached error doesn't count.
 */
export function isNotFound(q: QueryState): boolean {
  return q.error instanceof ApiError && q.error.status === 400 && q.isFetchedAfterMount && !q.isFetching;
}

interface Options {
  /** Where to send the user. Defaults to the not-found page. */
  to?: string;
  /** Error toast title, e.g. "Collection not found". */
  toast?: string;
  /** False while the redirect doesn't apply yet (e.g. parent not loaded). */
  enabled?: boolean;
}

/**
 * Leaves the page when its lookup comes back not found. Replaces the history entry so Back
 * doesn't bounce straight back here.
 */
export function useNotFoundRedirect(query: QueryState, { to = '/pagenotfound', toast, enabled = true }: Options = {}): void {
  const notFound = enabled && isNotFound(query);
  useEffect(() => {
    if (!notFound) return;
    // For app support: the toast and redirect leave no trace of which id failed.
    const error = query.error as ApiError;
    const reason = error.data?.errors?.[0]?.defaultMessage ?? error.body;
    console.error(`[NotFound] ${error.method} ${error.path} → ${error.status} (${reason}); redirecting to ${to}`);
    if (toast) notifyError({ title: toast, description: 'It may have been removed, or you may not have access to it.' });
    navigateToAngular(to, { replace: true });
  }, [notFound]); // eslint-disable-line react-hooks/exhaustive-deps
}
